/**
 * Her word when the scale's reading reaches this phone.
 *
 * The bridge posts to the server; the server pushes to the lock screen; and a
 * few seconds or a few hours later the row arrives here through the ordinary
 * sync pull, along with everything else that changed. That arrival is the
 * moment worth a line — he weighed himself, opened the app, and the number is
 * already there without his hand — so the sync engine raises an event for it
 * and this module decides whether she speaks.
 *
 * Three rules. Only today's readings: a week of history pulled onto a fresh
 * phone is a backfill, not news. Once per reading: the edit stamp is
 * remembered per account in Dexie meta, so the same row merged twice (two
 * pull pages, a retry) says nothing the second time. And the register is the
 * catalogue's, not this file's — dry mode gets its dry pool, and `sassToast`
 * falls back to the plain sentence when neither has anything.
 *
 * Decoupled from the engine by a DOM event rather than an import so that the
 * sync engine, which everything else already depends on, does not also grow
 * a dependency on the toast layer.
 */

import { deviceTimeZone, localDateString } from '@gramas/shared'
import type { LocalBodyMeasurement } from '../db/dexie.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { sassToast } from '../sass/toast.js'
import { openBodySheet } from '../screens/sheets/bodySheetStore.js'
import { currentProfile } from '../screens/useProfile.js'

/** Raised by the sync engine with the merged row as `detail`. */
export const SCALE_READING_EVENT = 'gramas:scale-reading'

export function announceScaleReading(row: LocalBodyMeasurement): void {
  window.dispatchEvent(new CustomEvent<LocalBodyMeasurement>(SCALE_READING_EVENT, { detail: row }))
}

function metaKey(): string {
  return `glitime.scale.${currentProfile()?.id ?? 'anon'}`
}

async function speak(row: LocalBodyMeasurement): Promise<void> {
  if (row.source !== 'scale' || row.deletedAt != null || row.weightKg == null) return
  if (row.measuredOn !== localDateString(new Date(), deviceTimeZone())) return

  const key = metaKey()
  const spoken = await getMeta<string | null>(key, null)
  if (spoken === row.clientEditedAt) return
  await setMeta(key, row.clientEditedAt)

  const weight = `${row.weightKg.toFixed(1).replace('.', ',')} kg`
  sassToast(
    'scale_spoke',
    { weightKg: row.weightKg, bodyFatPct: row.bodyFatPct ?? null },
    `Svarstyklės atsiuntė ${weight}.`,
    { label: 'Kūno sudėtis', run: () => openBodySheet() },
  )
}

/** Mount once, from the app shell. Returns the teardown. */
export function installScaleAnnouncer(): () => void {
  const onReading = (event: Event) => {
    void speak((event as CustomEvent<LocalBodyMeasurement>).detail)
  }
  window.addEventListener(SCALE_READING_EVENT, onReading)
  return () => window.removeEventListener(SCALE_READING_EVENT, onReading)
}
